import {
    createContext,
    memo,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { Routes as AppRoutes } from "@/constants/routes";

import { useGender } from "./GenderProvider";

export const enum Themes {
    Male = "male",
    Female = "female",
}

export const enum AdditionalThemes {
    LightMale = "light-male",
}

export interface ThemeContextType {
    theme: Themes;
    setTheme: (theme: Themes) => void;
    isLightTheme: boolean;
    isLightMaleTheme: boolean;
}

export const ThemeContext = createContext<ThemeContextType>({
    setTheme: () => null,
    theme: Themes.Male,
    isLightTheme: false,
    isLightMaleTheme: false,
});

const ThemeProvider = ({ children }: PropsWithChildren) => {
    const { isFemale } = useGender();

    const isFunnelFox = useMemo(() => {
        return window.location.pathname.includes(AppRoutes.FFPaywall);
    }, []);
    const isPromoCodeFlow = useMemo(
        () => window.location.pathname.includes(AppRoutes.RedeemCode),
        []
    );
    const isNyscFlow = useMemo(() => window.location.pathname.includes(AppRoutes.Nysc), []);

    // Initialize theme with the correct value from the start to avoid color flashing
    const themeValue = useMemo(() => {
        if (isFemale || isFunnelFox || isPromoCodeFlow || isNyscFlow) {
            return Themes.Female;
        }
        return Themes.Male;
    }, [isFemale, isFunnelFox, isPromoCodeFlow, isNyscFlow]);

    const [theme, setTheme] = useState<Themes>(themeValue);

    // Update theme state when theme value changes
    useEffect(() => {
        setTheme(themeValue);
    }, [themeValue]);

    useEffect(() => {
        document.body.classList.remove(theme === Themes.Male ? Themes.Female : Themes.Male);
        document.body.classList.add(theme);

        if (isFunnelFox) {
            if (isFemale) {
                document.body.classList.remove(AdditionalThemes.LightMale);
            } else {
                document.body.classList.add(AdditionalThemes.LightMale);
            }
        }
    }, [theme, isFunnelFox, isFemale]);

    const isLightTheme = useMemo(() => theme === Themes.Female, [theme]);
    const isLightMaleTheme = useMemo(
        () => theme === Themes.Female && !isFemale && isFunnelFox,
        [theme, isFemale, isFunnelFox]
    );

    return (
        <ThemeContext.Provider
            value={{
                setTheme,
                theme,
                isLightTheme,
                isLightMaleTheme,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    return useContext(ThemeContext);
};

export default memo(ThemeProvider);
